// hint.ts — the B-arm ("tutorial") onboarding hints: off-skew handwritten scribbles with a hand-drawn
// arrow that strokes itself in toward each reveal as it lands. One themed prompt per phase:
//   1. Mash    — lipstick rose + a kiss print   (flirt)         → the mash buttons
//   2. Persona — lilac + winking brows          (tile character) → the left rail
//   3. Juice   — amber + an impact burst        (punch/fx)       → the right rail
//   4. Box     — teal + a pencil                (write your own)  → the type-a-word field
// All decorative (aria-hidden, pointer-events:none — never block a click). Shown on reveal, wiped
// the moment the player fires the next word. Mash sits absolutely inside the panel; the rail/box
// prompts are position:fixed and measured off their target at reveal (the rails clip their overflow).

export interface Hint {
  el: HTMLElement;
  reveal: () => void;
  hide: () => void;
}

// ── doodles (hand-drawn, ~matching the kiss line-weight) ──────────────────────────────────────────
const KISS = `
  <svg class="onb-kiss" viewBox="0 0 100 80" aria-hidden="true">
    <path class="onb-kiss-upper" d="M12 38 C 18 24, 28 18, 34 22 C 40 25, 45 30, 50 34 C 55 30, 60 25, 66 22 C 72 18, 82 24, 88 38 C 76 34, 62 33, 50 38 C 38 33, 24 34, 12 38 Z" />
    <path class="onb-kiss-lower" d="M12 39 C 24 42, 38 43, 50 40 C 62 43, 76 42, 88 39 C 84 57, 68 67, 50 67 C 32 67, 16 57, 12 39 Z" />
  </svg>`;

// winking brows — two raised eyebrows, an open eye + a wink. The personality effect literally gives
// tiles ASCII brows, so the brows are its signature.
const BROWS = `
  <svg class="onb-doodle onb-brows" viewBox="0 0 96 56" aria-hidden="true">
    <path d="M10 20 Q 26 6, 42 20" />
    <path d="M54 20 Q 70 6, 86 20" />
    <circle cx="26" cy="36" r="4" />
    <path d="M54 34 Q 70 44, 86 34" />
  </svg>`;

// impact burst — spikes radiating from a centre, a comic "pow" for the juice/punch.
const BURST = `
  <svg class="onb-doodle onb-burst" viewBox="0 0 84 84" aria-hidden="true">
    <path d="M42 6 L42 30 M42 78 L42 54 M6 42 L30 42 M78 42 L54 42
             M17 17 L34 34 M67 67 L50 50 M67 17 L50 34 M17 67 L34 50" />
    <circle cx="42" cy="42" r="7" />
  </svg>`;

// pencil — a diagonal pencil with a graphite tip + a little scribble, for "write your own".
const PENCIL = `
  <svg class="onb-doodle onb-pencil" viewBox="0 0 88 64" aria-hidden="true">
    <path class="onb-pencil-body" d="M30 46 L58 14 L70 24 L42 56 Z" />
    <path class="onb-pencil-tip" d="M30 46 L42 56 L26 60 Z" />
    <path class="onb-pencil-scribble" d="M14 60 Q 34 52, 54 60" />
  </svg>`;

// ── arrows (one line that draws on + a head that snaps on after) ──────────────────────────────────
const ARROW_RIGHT = `
  <svg class="onb-arrow" viewBox="0 0 110 70" aria-hidden="true">
    <path class="onb-arrow-line" pathLength="1" d="M6 40 C 26 30, 50 30, 72 38 C 80 41, 88 40, 96 36" />
    <path class="onb-arrow-head" d="M96 36 L 82 29 M96 36 L 86 48" />
  </svg>`;
// points up — for the rail prompts, which sit BENEATH their rail and point back up into it.
const ARROW_UP = `
  <svg class="onb-arrow onb-arrow-up" viewBox="0 0 64 96" aria-hidden="true">
    <path class="onb-arrow-line" pathLength="1" d="M40 90 C 34 62, 26 36, 32 10" />
    <path class="onb-arrow-head" d="M32 10 L 20 26 M32 10 L 44 24" />
  </svg>`;

/** Wire the shared reveal/hide lifecycle onto a hint element. `place` runs once, right before the
 *  reveal animation, to position a fixed hint off its (now-visible) target. */
function lifecycle(el: HTMLElement, place?: () => void): Hint {
  let revealed = false;
  return {
    el,
    reveal(): void {
      if (revealed) return;
      revealed = true;
      place?.();
      el.classList.remove("onb-hidden");
      void el.offsetWidth; // reflow so the draw-on plays from the shown state
      el.classList.add("onb-in");
    },
    hide(): void {
      if (!revealed) {
        el.remove();
        return;
      }
      el.classList.add("onb-out");
      window.setTimeout(() => el.remove(), 380);
    },
  };
}

function make(cls: string, html: string): HTMLElement {
  const el = document.createElement("div");
  el.className = `onb-hint ${cls} onb-hidden`;
  el.setAttribute("aria-hidden", "true");
  el.innerHTML = html;
  return el;
}

/** Phase 1 — "Mash a button to get started!" — sits absolutely inside the mash panel (its context). */
export function createMashHint(): Hint {
  const el = make(
    "onb-mash",
    `<div class="onb-textcol"><span class="onb-note">Mash a button<br>to get started!</span>${KISS}</div>${ARROW_RIGHT}`,
  );
  return lifecycle(el);
}

/** Phase 2 — rail prompts. Each hangs BENEATH its rail with an up-arrow reaching back into it.
 *  Appended to <body>, positioned:fixed off the rail's rect at reveal (rails clip their overflow). */
export function createRailHint(kind: "pers" | "juice", rail: HTMLElement): Hint {
  const isPers = kind === "pers";
  const note = isPers ? "give 'em some<br>attitude" : "crank up<br>the juice";
  const doodle = isPers ? BROWS : BURST;
  const el = make(
    `${isPers ? "onb-pers" : "onb-juice"} onb-fixed onb-under`,
    `${ARROW_UP}<div class="onb-textcol"><span class="onb-note">${note}</span>${doodle}</div>`,
  );
  document.body.appendChild(el);
  return lifecycle(el, () => {
    const r = rail.getBoundingClientRect();
    el.style.left = `${Math.round(r.left + 22)}px`; // under the rail's action buttons
    el.style.top = `${Math.round(r.bottom - 20)}px`; // just beneath the rail; the arrow reaches up into it
  });
}

/** Phase 3 — "type in your own word" — note in the bottom-left quadrant, plus a long curved arrow
 *  (a full-viewport overlay whose path is computed at reveal) that hooks up and points RIGHT into the
 *  field's left edge (the cursor). Two elements so the arrow can span the whole gap precisely. */
export function createBoxHint(inputWrap: HTMLElement): Hint {
  const el = make(
    "onb-box onb-fixed",
    `<div class="onb-textcol"><span class="onb-note">type in<br>your own word</span>${PENCIL}</div>`,
  );
  el.style.left = "48px";
  el.style.bottom = "56px";
  document.body.appendChild(el);

  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("class", "onb-box-arrow onb-hidden");
  svg.setAttribute("aria-hidden", "true");
  const line = document.createElementNS(NS, "path");
  line.setAttribute("class", "onb-arrow-line");
  line.setAttribute("pathLength", "1");
  const head = document.createElementNS(NS, "path");
  head.setAttribute("class", "onb-arrow-head");
  svg.append(line, head);
  document.body.appendChild(svg);

  let revealed = false;
  return {
    el,
    reveal(): void {
      if (revealed) return;
      revealed = true;
      // Un-hide FIRST so the note has a real rect. The overlay SVG is a full-viewport unitless canvas
      // → path coords ARE viewport px.
      el.classList.remove("onb-hidden");
      svg.classList.remove("onb-hidden");
      const nr = el.getBoundingClientRect();
      const tx = Math.round(nr.right - 14); // tail: just off the note's top-right
      const ty = Math.round(nr.top - 4);
      // Target the field's LEFT edge. Its stamp animation transforms it, so getBoundingClientRect
      // lies mid-flight — read the transform-independent LAYOUT box (offsetLeft/Top via offsetParent).
      const op = (inputWrap.offsetParent as HTMLElement | null) ?? document.body;
      const opr = op.getBoundingClientRect();
      const gx = Math.round(opr.left + inputWrap.offsetLeft + 6);
      const gy = Math.round(opr.top + inputWrap.offsetTop + inputWrap.offsetHeight / 2);
      line.setAttribute("d", `M${tx} ${ty} C ${tx - 26} ${ty - 190}, ${gx - 150} ${gy + 24}, ${gx} ${gy}`);
      head.setAttribute("d", `M${gx} ${gy} L ${gx - 17} ${gy - 9} M${gx} ${gy} L ${gx - 17} ${gy + 9}`);
      void el.offsetWidth;
      el.classList.add("onb-in");
      svg.classList.add("onb-in");
    },
    hide(): void {
      if (!revealed) {
        el.remove();
        svg.remove();
        return;
      }
      el.classList.add("onb-out");
      svg.classList.add("onb-out");
      window.setTimeout(() => {
        el.remove();
        svg.remove();
      }, 400);
    },
  };
}
